// ══════════════════════════════════════════════════════════════════════════════════════
// LA FICHE TIERS — consulter, créer, modifier un tiers (« L'en-tête et les adresses »)
//
// Il n'y a qu'UNE fiche tiers, c'est celle-ci — refondue le 2026-09-15 à la place de l'ancienne
// partner.component et de ses sous-composants app-addresses et app-contacts, supprimés.
//
// Ce fichier est AUTONOME : il n'hérite de rien, n'importe aucun socle partagé, et toute la
// logique — chargement, formulaire, rôles, cufs, adresses, géocodage, enregistrement — vit ici.
// Les relations du tiers (adresses comprises) sont manipulées par PartnerLinksForm
// (_models/common.ts), qui n'a pas d'autre client.
//
// DEUX MONTAGES, un seul gabarit et une seule feuille :
//   • en FENÊTRE, au clic sur le nom du client d'une commande (QuotationComponent.
//     openPartnerDialog), depuis le sélecteur de tiers (SearchPartnerComponent.
//     openPartnerDialog), depuis le fabricant d'un article (productTpl, product-tpl-general)
//     et depuis une relation (openRelated, ci-dessous) — MatDialogRef et MAT_DIALOG_DATA sont
//     injectés, l'id vient de dialogData ;
//   • en PAGE, sur /common/partner/:id (et /common/partner pour un tiers neuf) depuis la liste
//     des tiers — aucun MatDialogRef, l'id vient de la route, la barre de menu de l'application
//     est rendue au-dessus et « Fermer » ramène à la liste. Ce qui diffère tient dans
//     `isDialog`, la classe d'hôte `in-dialog` et close().
//
// LE PARTI PRIS, en un mot : UNE SEULE COLONNE.
// Pas de découpe verticale de la fenêtre. On lit de haut en bas :
//   1. L'EN-TÊTE porte TOUT le tiers, en trois colonnes pleine hauteur (60 / 20 / 20) — son nom, ses rôles,
//      ses neuf champs d'identité, ses champs utilisateur (forme juridique, encours, poids,
//      taille, date de décès) et son mémo ; SES PIÈCES JOINTES ; les actes de la fenêtre puis SES
//      RELATIONS (les lignes c_partner_partner hors adresses : gérant, tiers payeur,
//      apporteur…). Rien de l'identité ne descend plus bas.
//   2. LES ADRESSES occupent tout le reste, une par bande pleine largeur : les informations à
//      gauche, la carte à droite. Une bande entière à 1536 px, deux et demie à 1920 (mesuré
//      le 2026-10-23) ; au-delà, la liste défile.
//
// Le style est celui de la fenêtre « Programmation de livraison » : papier blanc franc, filets
// de 1 px, aucune ombre, aucun aplat, l'ambre pour seul accent. _delivery-ds.scss est un
// partial local à delivery — il n'est pas importé, ce qui sert est recopié dans le .scss.
// ══════════════════════════════════════════════════════════════════════════════════════
import { BreakpointObserver } from '@angular/cdk/layout';
import { NgTemplateOutlet } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, HostBinding, HostListener, OnDestroy, OnInit, QueryList, ViewChildren, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, CanDeactivateFn, Params, Router } from '@angular/router';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, take } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { CommonService } from '@app/common/common.service';
import { PartnerService } from '@app/common/partner/partner.service';
import { RepriseOrthopComponent } from '@app/common/partner/reprise-orthop/reprise-orthop.component'; // REPRISE ORTHOP, TEMPORAIRE
import { ValuesService } from '@app/common/values/values.service';
import { AlertService } from '@app/shared/alert.service';
import { AttachService } from '@app/shared/attach/attach.service';
import { ImageCarouselComponent } from '@app/shared/attach/image-carousel/image-carousel.component';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { CufComponent } from '@app/shared/cuf/cuf.component';
import { GeoAddress, GeoOutcome, GeoService } from '@app/shared/geo.service';
import { NEW_SALE_DOCUMENTS, NEW_SALE_KINDS, NewSaleKind, openSaleForCustomer } from '@app/sales/quotation/new-sale.service';
import { MiniMapComponent } from '@app/shared/mini-map/mini-map.component';
import { SearchPartnerComponent } from '@app/shared/search-partner/search-partner.component';
import { SmartSelectComponent } from '@app/shared/smart-select/smart-select.component';
import { pickedDateStr, yearsBefore } from '@app/shared/time';
import * as common from '@app/shared/_models/common';
import { PartnerLinksForm } from '@app/shared/_models/common';
import * as constants from '@app/shared/constants';
import { SALES_DEFAULT_CUSTOMER_ORDERS_YEARS } from '@app/shared/constants-server';
import { environment } from '@app/../environments/environment';

// Pseudo-mobile sous 1100 px d'écran (décision JP, 23/10/2026) ; la classe de surface élargit la fenêtre à tout l'écran (voir le .scss).
const COMPACT_QUERY = '(max-width: 1099.98px)';
const COMPACT_PANE = 'p2-pane-compact';

/** Une relation à l'écran : une ligne c_partner_partner du tiers, et ce qu'on en montre. */
interface RelationRow {
	key: string;				// child/role — la clé primaire de c_partner_partner, pour le track
	childId: number;
	role: number;
	roleName: string;
	label: string;				// nom du tiers rattaché, ou son adresse à défaut
	isDefault: boolean;
	busy: boolean;				// une écriture est en vol pour cette ligne (ajout ou retrait)
}

@Component({
	selector: 'app-partner',
	templateUrl: './partner.component.html',
	styleUrl: './partner.component.scss',
	// FormsModule n'est PAS importé : pas un seul ngModel ici, tout passe par le formulaire
	// réactif — y compris le sous-formulaire d'ajout d'adresse, qui est une ligne de la
	// FormArray comme les autres et non une poignée de champs à part.
	// app-image-carousel est le module d'affichage des pièces jointes DÉJÀ en service (fiche
	// article, programmation de livraison) : renommage, séquence, image par défaut, aperçu,
	// téléchargement et suppression y sont câblés. On le reprend tel quel plutôt que d'en
	// réécrire une version au rabais ; seule sa taille est reprise, en :host ::ng-deep.
	// app-search-partner est le sélecteur de tiers de toute l'application : c'est par lui qu'on
	// choisit le tiers à rattacher (volet « Relations » de l'en-tête).
	// app-menu-common est la barre de menu de l'application : rendue en mode PAGE seulement,
	// comme sur toute page routée (la liste des tiers, l'ancienne fiche).
	imports: [ReactiveFormsModule, NgTemplateOutlet, MatIconModule, MatTooltipModule, CufComponent, MiniMapComponent, SmartSelectComponent, ImageCarouselComponent, SearchPartnerComponent, MenuCommonComponent, RepriseOrthopComponent],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: true,
})
export class PartnerComponent implements OnInit, OnDestroy {
	private fb = inject(FormBuilder);
	private route = inject(ActivatedRoute);
	private partnerService = inject(PartnerService);
	private commonService = inject(CommonService);
	private valuesService = inject(ValuesService);
	private alert = inject(AlertService);
	private geoService = inject(GeoService);
	private attachService = inject(AttachService);
	private http = inject(HttpClient);
	private dialog = inject(MatDialog);
	private router = inject(Router);
	public dialogRef = inject<MatDialogRef<PartnerComponent>>(MatDialogRef, { optional: true });
	public dialogData: any = inject(MAT_DIALOG_DATA, { optional: true });

	/** FENÊTRE ou PAGE : le seul critère est la présence du MatDialogRef — un composant routé
	 *  n'en reçoit aucun. La classe d'hôte `in-dialog` porte la différence côté feuille : en
	 *  page, l'hôte est un maillon flex de app-root (barre de menu + feuille, pied de page de
	 *  l'application dessous) ; en fenêtre, c'est la surface Material qui impose la hauteur. */
	get isDialog(): boolean { return !!this.dialogRef; }
	@HostBinding('class.in-dialog') get hostInDialog(): boolean { return this.isDialog; }

	private breakpoints = inject(BreakpointObserver);
	/** Écran de moins de 1100 px : une colonne, pièces et relations pliées au-dessus des adresses, actes dans une barre collée en haut. */
	public compact = false;
	@HostBinding('class.compact') get hostCompact(): boolean { return this.compact; }
	public docsOpen = false;								// volets pliés du mode compact
	public relOpen = false;
	@ViewChildren(MiniMapComponent) private maps?: QueryList<MiniMapComponent>;
	private readonly resized$ = new Subject<void>();
	@HostListener('window:resize') onWindowResize(): void { this.resized$.next(); }

	/** Hauteur de la carte, en PIXELS. app-mini-map calcule sa mosaïque de tuiles et le centrage
	 *  du marqueur depuis cette entrée, pas depuis sa boîte CSS : la valeur doit rester égale à
	 *  la hauteur réservée dans le .scss, sinon il manque une bande de tuiles en bas et l'épingle
	 *  est décentrée.
	 *  268 et non 248 : la carte est à fond perdu et AVALE les marges hautes et basses de la
	 *  bande (`--adr-h` 248 + 2 × `--adr-pad` 10), pour aller d'un filet de séparation à l'autre.
	 *  Les deux tiers de texte, eux, restent à 248 (208 avant la rangée des cufs d'adresse,
	 *  2026-09-15). */
	public readonly MAP_H = 268;

	public id = 0;
	public eventtyp: number = constants.EVENTTYP_INSERT;		// 1 création, 2 mise à jour
	public activities: any[] = [];							// c_values 5 — l'activité du tiers
	public cufsStructure: common.Cuf[] = [];				// cufs du TIERS (composant 2) : forme juridique, encours, poids, taille…
	// Rangée C : la tête (forme juridique, encours — ordre du référentiel) s'aligne sous l'activité et le paiement, la queue suit.
	// Tableaux posés à l'arrivée et non getters : un tableau neuf à chaque cycle rejouerait ngOnChanges (applyRules) d'app-cuf.
	public cufsHead: common.Cuf[] = [];
	public cufsTail: common.Cuf[] = [];

	// Bloc « Identité professionnelle » (composant 9) : replié dès que la forme juridique choisie
	// est celle d'un particulier (`is_private` dans le référentiel des formes), visible sinon —
	// forme non renseignée comprise. Le contenu des champs n'entre pas en ligne de compte. La
	// règle vit dans CufComponent.isLegalBlockVisible.
	public legalCufs: common.Cuf[] = [];
	public legalForms: any[] = [];
	public countries: any[] = [];							// c_values 36 — tous les pays ; la liste d'une adresse ne propose que les actifs
	private countryItemsByCode = new Map<string, { label: string; value: string }[]>();

	get legalBlockVisible(): boolean {
		return CufComponent.isLegalBlockVisible(this.partnerCufGroup, this.legalForms);
	}
	public addressCufs: common.Cuf[] = [];					// cufs des ADRESSES (composant 1) — servent à bâtir une ligne neuve
	// La structure d'adresse est rendue en DEUX endroits : le SIRET, numéro d'établissement,
	// dans la rangée géo après la Base Adresse Nationale (et seulement pour un professionnel) ;
	// tous les autres cufs du composant 1 (niveau, code porte… selon la base) dans une rangée
	// à eux, sous les coordonnées, pour chaque adresse. Vides tant que le référentiel ne porte
	// rien au composant 1 : rien n'est rendu, et aucun control ne manque.
	public siretCufs: common.Cuf[] = [];
	public otherAddressCufs: common.Cuf[] = [];
	public formGroup!: FormGroup;
	/** Le tiers TEL QUE LE SERVEUR L'A RENDU au dernier chargement — donc au dernier
	 *  enregistrement. C'est CET état que la fenêtre rend à la commande (close), jamais la
	 *  saisie : le formulaire porte des lignes DELETE encore présentes, des adresses neuves à
	 *  l'id négatif, et ses lignes de qualité n'ont pas d'objet lié — une commande qui le
	 *  relisait pouvait partir avec addr_deliv = -1 ou retomber sur la première adresse. */
	private persisted: common.Partner | null = null;
	/** Rechargement qui suit un enregistrement : une fermeture demandée pendant attend l'état
	 *  frais, sinon la commande recevrait le tiers d'AVANT — sans l'adresse qu'on vient de lui
	 *  ajouter pour elle. */
	private reloading = false;
	private closeWhenReloaded = false;
	public loading = true;
	public saving = false;

	// --- Géolocalisation : mêmes cufs Latitude/Longitude que la fiche officielle ---
	public readonly CUF_LAT = constants.CUF_ADDRESS_LAT;
	public readonly CUF_LNG = constants.CUF_ADDRESS_LNG;
	/** État du géocodage PAR ADRESSE, clé = son `child` (l'id de la ligne, provisoire négatif
	 *  pour une neuve) — jamais sa position à l'écran : la liste est re-triée à chaque lecture
	 *  (facturation d'abord), et une réponse BAN arrivée après un clic « Facturation » se
	 *  posait sur une AUTRE adresse, marquée modifiée. Le gabarit lit par l'index (geoBusy,
	 *  geoErr, banLabel…), la résolution index → child se fait ici. */
	private geoPending = new Set<number>();
	/** Adresses localisées à l'ouverture (par child) : pas une saisie, la fiche reste « non modifiée » ; prepareAddresses les marque à l'envoi. */
	private autoGeo = new Set<number>();
	private geoInfo = new Map<number, GeoAddress>();
	private geoError = new Map<number, string>();
	/** BAN muette (délai, réseau, 429, 5xx) : l'adresse n'est pas en cause, un second clic suffit. La NRT y lit « injoignable ». */
	public readonly GEO_INJOIGNABLE = 'BAN injoignable — réessayez';

	/** Les trois rôles, dans l'ordre où on les lit : le plus fréquent d'abord. Banque (-12) n'en est pas :
	 *  il se pose dans PostgreSQL, et un tiers banque le garde à l'enregistrement (toggleRole ne touche que sa pastille). */
	public readonly roleChips: { id: number; label: string }[] = [
		{ id: constants.PARTNER_ROLE_CUSTOMER, label: 'Client' },
		{ id: constants.PARTNER_ROLE_SUPPLIER, label: 'Fournisseur' },
		{ id: constants.PARTNER_ROLE_MANUFACTURER, label: 'Fabricant' },
	];

	// --- Relations (colonne 3 de l'en-tête) ----------------------------------------------
	// Les lignes c_partner_partner du tiers HORS adresses, lues par la route dédiée /links
	// (qui charge le tiers rattaché, donc son nom) et ÉCRITES AU CLIC, comme les pièces
	// jointes — par leur propre event, jamais avec le formulaire. Le formulaire porte bien ces
	// lignes (lnk_partner_partners, chargé avec le tiers) mais il ne s'en sert pas : elles y
	// restent UNCHANGED, et le serveur ne touche pas à ce qui est UNCHANGED.
	/** c_values 26 — les rôles de relation ; `data.applies_to` dit à quoi chacun s'applique. */
	public relationRoles: any[] = [];
	public relationRoleItems: { label: string; value: number }[] = [];
	/** Les relations du tiers, telles que le serveur les rend — plus les lignes en vol. */
	public relations: RelationRow[] = [];
	public relationsLoading = false;
	/** Le tiers choisi dans le sélecteur, en attente d'un titre et d'un clic. */
	public newRelPartner: any = null;
	public readonly newRelRole = new FormControl<number | null>(null);
	/** Remise à zéro du sélecteur après un ajout : c'est SON reset qu'il faut appeler, pas
	 *  seulement vider newRelPartner — il relie sa recherche au champ qu'il vient de recréer. */
	public readonly relSearchReset = new Subject<boolean>();

	// --- Pièces jointes des ADRESSES, et du TIERS ----------------------------------------
	// Une adresse EST une ligne c_partner : ses pièces jointes se rattachent donc au même
	// couple (objecttyp PARTNER, objectid = id de la ligne d'adresse) que n'importe quel
	// tiers. Rien de spécifique n'est ajouté côté modèle — on emprunte le service générique.
	// Les pièces du tiers LUI-MÊME (colonne 2 de l'en-tête) suivent exactement le même
	// chemin, avec objectid = l'id du tiers : les ids de c_partner sont uniques, adresses et
	// tiers ne peuvent pas se confondre dans la table ci-dessous.
	public readonly ATTACH_OBJECTTYP = constants.ObjectType.PARTNER;
	/** Pièces jointes MÉDIA, par id de ligne c_partner (adresse ou tiers). */
	public attachs = new Map<number, common.Attach[]>();
	/** Mémo de chaque ligne, adresse ou tiers (l'attach « is_default_memo »), un control par ligne. Un control
	 *  et non une simple chaîne : sur une zone de texte, une liaison [value] réécrite à chaque
	 *  frappe renvoie le curseur à la fin du texte. */
	private memoCtrls = new Map<number, FormControl>();
	/** Zone de mémo d'une ligne PAS ENCORE ENREGISTRÉE. Une seule instance, désactivée, partagée :
	 *  une ligne neuve n'a qu'un id provisoire, négatif (PartnerLinksForm.addAddress), que la
	 *  base ne connaît pas — un mémo posté dessus serait rattaché à un objectid -1, orphelin à
	 *  jamais. Comme rien ne peut s'attacher à une ligne sans id, la zone est ici close et vide,
	 *  et c'est explicite. */
	private readonly memoUnsaved = new FormControl({ value: '', disabled: true });
	/** Dernière valeur ENREGISTRÉE du mémo : sert à n'écrire que ce qui a changé. */
	private memosSaved = new Map<number, string>();
	/** L'ID DE L'ATTACH mémo de chaque adresse : c'est lui que l'event 603 met à jour. 0 = aucun
	 *  en base, le premier envoi le crée (branche INSERT du serveur) et l'id rendu est retenu ;
	 *  clé ABSENTE = pièces pas encore lues, on ne sait pas encore s'il existe. Poster `id: 0`
	 *  sans cette mémoire, c'est un INSERT à chaque envoi : autant de lignes is_default_memo que
	 *  de frappes, et le texte « perdu » au rechargement (la plus ancienne était relue). */
	private memoAttachIds = new Map<number, number>();
	/** Envoi en vol, par adresse : affiché dans la bande, et garde contre un 2ᵉ POST en id 0. */
	public memoSaving = new Set<number>();
	public uploading = new Set<number>();
	/** URL d'objet des vignettes : à révoquer au démontage, sinon les blobs fuient. */
	private objectUrls: string[] = [];

	private subs: Subscription[] = [];

	// ─────────────────────────────────────────────────────────────────────────────────
	// CYCLE DE VIE
	// ─────────────────────────────────────────────────────────────────────────────────
	ngOnInit(): void {
		// Première émission synchrone : la fiche naît déjà dans le bon mode, sans bascule visible.
		this.subs.push(this.breakpoints.observe(COMPACT_QUERY).subscribe((s) => this.setCompact(s.matches)));
		// app-mini-map calcule ses tuiles sur la largeur lue au rendu : une fenêtre redimensionnée (téléphone tourné) les redessine.
		this.subs.push(this.resized$.pipe(debounceTime(150)).subscribe(() => this.renderMaps()));
		this.id = this.dialogData ? Number(this.dialogData.id) : Number(this.route.snapshot.paramMap.get('id'));
		if (!this.id || isNaN(this.id)) this.id = 0;

		if (!this.isDialog) {
			// EN PAGE, l'id est celui de la route et peut CHANGER sans que le composant soit
			// recréé : deux navigations vers /common/partner/:id se partagent l'instance (même
			// entrée de route), seul le paramètre bouge. On suit donc le paramètre, pas seulement
			// son instantané — sinon la fiche resterait sur le premier tiers. Le premier tour
			// est celui de l'instantané ci-dessus : on ne recharge que sur un id DIFFÉRENT.
			this.subs.push(this.route.paramMap.subscribe((pm) => {
				const id = Number(pm.get('id')) || 0;
				if (id === this.id) return;
				this.id = id;
				// C'est un AUTRE tiers : la feuille en place est la sienne, pas celle du nouveau —
				// on la retire, loadPartner remet l'écran d'attente (loading = !formGroup).
				this.formGroup = undefined!;
				if (id === 0) { this.formGroup = this.emptyPartner(); this.eventtyp = constants.EVENTTYP_INSERT; this.ensureCufControls(); }
				else this.loadPartner();
			}));
		}

		// En fenêtre, Échap = la croix et le clic extérieur ne fait rien (JP, 23/10). close() confirme une saisie non
		// enregistrée et rend le tiers persisté à l'appelant, sans quoi la commande perdrait son client.
		if (this.dialogRef) {
			this.dialogRef.disableClose = true;
			this.subs.push(this.dialogRef.keydownEvents().pipe(filter((e) => e.key === 'Escape')).subscribe(() => this.close()));
		}

		this.subs.push(this.valuesService.getValues$(constants.C_VALUES_CUSTOMER_ACT).subscribe((d: any) => (this.activities = d ?? [])));
		// Un référentiel 26 absent (base sans dml.sql) n'empêche rien : les rôles s'affichent
		// alors par leur numéro, et la liste d'ajout est vide.
		this.subs.push(this.valuesService.getValues$(constants.C_VALUES_PARTNER_PARTNER_ROLE).pipe(catchError(() => of([]))).subscribe((values: any) => {
			this.relationRoles = (values ?? []).filter((v: any) => v?.is_active !== false);
			// « Banque » (-34) ne s'ajoute pas ici : les banques d'un magasin se lient dans PostgreSQL (cash_transfer_store_banks.sql).
			this.relationRoleItems = this.relationRoles
				.filter((v: any) => v?.data?.applies_to !== 'address' && +v.id !== constants.PARTNER_PARTNER_ROLE_BANK)
				.map((v: any) => ({ label: v.name, value: +v.id }));
		}));
		this.subs.push(this.commonService.getCufs(constants.C_COMPONENT_PARTNER).subscribe((s: common.Cuf[]) => {
			this.cufsStructure = s; this.cufsHead = s.slice(0, 2); this.cufsTail = s.slice(2);
			this.ensureCufControls();
		}));
		this.subs.push(this.commonService.getCufs(constants.C_COMPONENT_PARTNER_LEGAL).subscribe((s: common.Cuf[]) => { this.legalCufs = s; this.ensureCufControls(); }));
		this.subs.push(this.valuesService.getValues$(constants.C_VALUES_LEGAL_FORM).subscribe((f: any) => (this.legalForms = f ?? [])));
		this.subs.push(this.valuesService.getValues$(constants.C_VALUES_COUNTRY).pipe(catchError(() => of([]))).subscribe((v: any) => {
			this.countries = v ?? [];
			this.countryItemsByCode.clear();
		}));
		this.subs.push(this.commonService.getCufs(constants.C_COMPONENT_ADDRESS).subscribe((s: common.Cuf[]) => {
			this.addressCufs = s;
			this.siretCufs = s.filter((c) => +c.id! === constants.CUF_ADDRESS_SIRET);
			this.otherAddressCufs = s.filter((c) => +c.id! !== constants.CUF_ADDRESS_SIRET);
			this.ensureAddressCufControls();
		}));

		// TIERS NEUF : le formulaire naît APRÈS les souscriptions ci-dessus. Quand les référentiels
		// sont déjà en cache (ValuesService : toute visite après la première), getCufs émet
		// SYNCHRONIQUEMENT à la souscription — ensureCufControls a alors tourné sur un formulaire
		// encore absent et n'a rien posé : les cellules app-cuf du tiers (poids, taille, forme
		// juridique, SIREN…) se retrouvaient sans control, la saisie partait dans le vide et le
		// tiers était enregistré sans aucun cuf. Le premier « Nouveau tiers » de la session, lui,
		// marchait (référentiels chargés en asynchrone, donc après le formulaire) — c'est le NRT
		// partnercreation qui l'a mesuré le 2026-09-15 : client 1 complet, clients 2 à 20 sans
		// cufs. D'où l'appel explicite une fois le formulaire créé.
		if (this.id === 0) { this.formGroup = this.emptyPartner(); this.loading = false; this.ensureCufControls(); }
		else this.loadPartner();
	}

	ngOnDestroy(): void {
		// La dernière frappe d'un mémo n'a peut-être pas eu sa seconde d'auto-enregistrement
		// (Échap, clic hors de la fenêtre, navigation) : elle part maintenant, AVANT le
		// désabonnement qui coupe le debounce — le POST, lui, n'est pas dans `subs`.
		for (const objectid of this.memoCtrls.keys()) this.saveMemo(objectid);
		this.subs.forEach((s) => s.unsubscribe());
		// Vignettes : sans révocation, les blobs restent en mémoire jusqu'au rechargement de la
		// page. On balaie les DEUX sources — celles qu'on a créées au dépôt (objectUrls) et celles
		// que le service pose lui-même, plus tard, sur chaque attach (generate_attachs_… charge les
		// vignettes de façon asynchrone : au moment où on range la liste, elles n'existent pas
		// encore, les collecter à ce moment-là n'en attraperait aucune).
		for (const list of this.attachs.values()) {
			for (const a of list) if (a.thumbnailLocalUrl) URL.revokeObjectURL(a.thumbnailLocalUrl);
		}
		this.objectUrls.forEach((u) => URL.revokeObjectURL(u));
	}

	/** Tiers neuf : mêmes controls et mêmes valeurs par défaut que la fiche officielle.
	 *  street1..country restent montés et partent nuls — la donnée vit dans les adresses. */
	private emptyPartner(): FormGroup {
		return this.fb.group({
			id: new FormControl(0),
			civility: new FormControl('0'),
			firstname: new FormControl(''),
			name: new FormControl(''),
			typ: new FormControl(1),
			typpay: new FormControl('0'),
			roles: this.fb.array([constants.PARTNER_ROLE_CUSTOMER]),			// client par défaut
			email: new FormControl(''),
			tel1: new FormControl(''),
			tel2: new FormControl(''),
			street1: new FormControl(''),
			street2: new FormControl(''),
			street3: new FormControl(''),
			zip: new FormControl(''),
			city: new FormControl(''),
			country: new FormControl(''),
			lnk_partner_partners: this.fb.array([]),	// les relations, adresses comprises (lignes de rôle -5)
			data: common.Partner.dataAsFormGroup(null),
		});
	}

	loadPartner(): void {
		// L'écran d'attente ne sert qu'au PREMIER chargement : le rechargement qui suit un
		// enregistrement ne doit pas faire disparaître la feuille, elle est déjà sous les yeux.
		this.loading = !this.formGroup;
		this.eventtyp = constants.EVENTTYP_UPDATE;
		this.subs.push(
			// take(1) : getPartnerAsFormGroup() ne complète JAMAIS (il vit sur les BehaviorSubject
			// de ValuesService). Sans lui, un rafraîchissement de référentiel ré-émet et remplace
			// le formulaire sous les doigts de l'utilisateur, saisie comprise.
			this.partnerService.getPartnerWithForm$(this.id).pipe(take(1)).subscribe({
				next: ({ partner, formGroup }) => {
					this.persisted = partner ?? null;
					this.formGroup = formGroup;
					this.restoreRadioStrings();
					this.ensureCufControls();
					this.ensureAddressCufControls();
					this.geoPending.clear(); this.geoInfo.clear(); this.geoError.clear(); this.autoGeo.clear();
					this.loading = false;
					this.geocodeMissing();
					this.loadAttachs();
					this.loadRelations();
					this.reloaded();
				},
				error: (e: any) => { this.loading = false; this.alert.error('ERREUR Serveur : ' + e); this.reloaded(); },
			})
		);
	}

	/** Fin d'un rechargement : la fermeture mise en attente pendant celui-ci part maintenant. */
	private reloaded(): void {
		this.reloading = false;
		if (this.closeWhenReloaded) { this.closeWhenReloaded = false; this.closeDialog(); }
	}

	/** Les boutons radio veulent des CHAÎNES ; le formulaire chargé (et celui qu'un envoi a
	 *  laissé en entiers) les portent en nombres — sans ce retour en chaîne, Civilité et
	 *  Paiement se vident de leur sélection à l'écran. */
	private restoreRadioStrings(): void {
		this.formGroup.controls['civility'].patchValue(String(this.formGroup.controls['civility'].value));
		this.formGroup.controls['typpay'].patchValue(String(this.formGroup.controls['typpay'].value));
	}

	// ─────────────────────────────────────────────────────────────────────────────────
	// EN-TÊTE — L'IDENTITÉ DU TIERS
	// ─────────────────────────────────────────────────────────────────────────────────
	get fullName(): string {
		const n = `${this.formGroup?.get('firstname')?.value ?? ''} ${this.formGroup?.get('name')?.value ?? ''}`.replace(/\s+/g, ' ').trim();
		return n || 'Nouveau tiers';
	}

	hasRole(role: number): boolean { return (this.formGroup?.value?.roles ?? []).includes(role); }

	/** Les rôles ne sont pas des controls mais une FormArray d'entiers : on la remplace. */
	toggleRole(role: number): void {
		const roles: number[] = this.formGroup.value.roles ?? [];
		if (roles.includes(role)) this.formGroup.setControl('roles', this.fb.array(roles.filter((v: number) => v !== role)));
		else this.formGroup.setControl('roles', this.fb.array([...roles, role]));
		this.formGroup.markAsDirty();
	}

	/** Groupe des cufs du TIERS : data.cufs de la ligne principale. */
	get partnerCufGroup(): FormGroup | null { return (this.formGroup?.get('data')?.get('cufs') as FormGroup) ?? null; }

	/** Un control par cuf de la structure. Les clés déjà présentes — cufs d'AUTRES composants
	 *  stockés sur la même ligne — sont laissées telles quelles : elles ne sont pas affichées
	 *  mais repartent à l'identique à la sauvegarde. */
	private ensureCufControls(): void {
		const grp = this.partnerCufGroup;
		if (!grp) return;
		for (const cuf of [...this.cufsStructure, ...this.legalCufs]) {
			if (cuf.id === undefined || cuf.id === null) continue;
			if (!grp.get('' + cuf.id)) grp.addControl('' + cuf.id, new FormControl(null));
		}
	}

	get saveLabel(): string { return this.saving ? 'Enregistrement…' : 'Enregistrer'; }

	public readonly ORDERS_YEARS = SALES_DEFAULT_CUSTOMER_ORDERS_YEARS;

	/** Rôle Client lu dans l'état EN BASE (persisted) : une pastille cochée mais pas enregistrée n'ouvre pas de commandes. */
	get canOpenOrders(): boolean {
		return this.isPersisted && (this.persisted?.roles ?? []).some((r) => +r === constants.PARTNER_ROLE_CUSTOMER);
	}

	private setCompact(on: boolean): void {
		if (on === this.compact) return;
		this.compact = on;
		if (on) this.dialogRef?.addPanelClass(COMPACT_PANE); else this.dialogRef?.removePanelClass(COMPACT_PANE);
		setTimeout(() => this.renderMaps());		// les cartes passent d'un tiers de bande à toute la largeur, ou l'inverse
	}

	private renderMaps(): void { this.maps?.forEach((m) => m.render()); }

	/** Saisie pas encore en base. Faux pendant le rechargement qui suit un enregistrement réussi : l'ancien formulaire est encore dirty. */
	get hasUnsavedChanges(): boolean { return !this.reloading && !!this.formGroup?.dirty; }

	/** Sélecteur de commandes filtré sur ce client depuis ORDERS_YEARS ans ; dates prises au clic (fiche restée ouverte la nuit). */
	openOrders(): void {
		// Grisé dans le gabarit, en page comme en fenêtre (JP, 23/10) : partir pendant le POST l'annulerait, et une saisie se perdrait sans un mot.
		if (this.saving || this.hasUnsavedChanges) return;
		const now = new Date();
		this.openPage(['/sales/quotations'], { customer: this.id, from: pickedDateStr(yearsBefore(now, this.ORDERS_YEARS)), to: pickedDateStr(now) });
	}

	/** Dossiers de location dont ce tiers est le client ou le bénéficiaire (patient d'une HAD, audit 31/10 #38) ; mêmes gardes qu'openOrders. */
	openRentals(): void {
		if (this.saving || this.hasUnsavedChanges) return;
		this.openPage(['/rental/payeur', this.id], { with_recipient: 'true' });
	}

	rentalsTip(): string {
		return this.hasUnsavedChanges ? 'Vous devez d’abord enregistrer la fiche avant de pouvoir ouvrir ses dossiers de location' : 'Tous les dossiers de location du tiers : payés par lui, ou dont il est le bénéficiaire';
	}

	private openPage(commands: unknown[], queryParams?: Params): void {
		if (!this.isDialog) { this.router.navigate(commands, { queryParams }); return; }
		// En fenêtre, naviguer sous la modale détruirait la commande (F1/F2/F4…) ouverte dessous : nouvel onglet.
		const url = this.router.serializeUrl(this.router.createUrlTree(commands, { queryParams }));
		if (!window.open(url, '_blank')) this.alert.error('Onglet bloqué par le navigateur — autorisez les pop-ups pour ce site');
	}

	public readonly NEW_SALE_DOCUMENTS = NEW_SALE_DOCUMENTS;
	public readonly NEW_SALE_KINDS = NEW_SALE_KINDS;

	/** F1, F2 ou F3 neuve, ce client déjà choisi ; mêmes gardes et même nouvel onglet en fenêtre qu'openOrders. */
	openNewSale(kind: NewSaleKind): void {
		if (this.saving || this.hasUnsavedChanges) return;
		openSaleForCustomer(this.router, this.alert, kind, this.id, this.isDialog);
	}

	saleTip(kind: NewSaleKind): string {
		return this.hasUnsavedChanges ? 'Vous devez d’abord enregistrer la fiche avant de pouvoir saisir une nouvelle pièce' : NEW_SALE_DOCUMENTS[kind].customerTip;
	}

	// ─────────────────────────────────────────────────────────────────────────────────
	// RELATIONS — les lignes c_partner_partner du tiers hors adresses
	//
	// ÉCRITES AU CLIC, COMME LES PIÈCES JOINTES. Un ajout poste une ligne INSERT (child = l'id
	// du tiers rattaché, lnk_child_partner null : le serveur ne touche pas à l'enfant, il pose
	// le lien), un retrait une ligne DELETE — le serveur n'efface que ce (parent, child,
	// rôle)-là, jamais le tiers. Chaque geste part aussitôt par savePartnerLinks$, qui repart
	// de l'état FRAIS du tiers sur le serveur et n'y greffe que cette ligne : ce que la fiche
	// a de modifié et pas encore enregistré n'est ni écrit ni perdu. La ligne se grise le temps
	// du voyage, puis la liste est RELUE depuis le serveur — c'est lui qui a raison.
	// ─────────────────────────────────────────────────────────────────────────────────

	/** Un rôle d'ADRESSE (-1, -2, -5, ou `applies_to: address` dans le référentiel) n'est pas
	 *  une relation : ces liens se lisent dans les bandes d'adresse. */
	private isRelationRole(role: number): boolean {
		if (constants.PARTNER_PARTNER_ROLES_ADDRESS.includes(role)) return false;
		return this.relationRoles.find((r: any) => +r.id === role)?.data?.applies_to !== 'address';
	}

	private relationRoleName(role: number): string {
		return this.relationRoles.find((r: any) => +r.id === role)?.name ?? ('Rôle ' + role);
	}

	get relationCount(): number { return this.relations.length; }

	get canAddRelation(): boolean {
		return this.isPersisted && !!this.newRelPartner?.id && this.newRelRole.value !== null && this.newRelRole.value !== undefined;
	}

	onNewRelPartner(p: any): void { this.newRelPartner = p ?? null; }

	/** Le nom d'un tiers rattaché ; une ligne sans nom (une adresse rattachée à un titre hors
	 *  adresse) se lit par sa voie. PAS DE VILLE : depuis la bascule c_partner_partner, un tiers
	 *  n'a plus de ville en propre (elle est sur ses adresses, que /links ne charge pas), et une
	 *  ville affichée à l'ajout puis disparue au rechargement se lirait comme un bug. Le numéro
	 *  du tiers, lui, est toujours là — c'est lui qui distingue deux homonymes. */
	private relLabelOf(p: any): string {
		const nom = `${p?.name ?? ''} ${p?.firstname ?? ''}`.replace(/\s+/g, ' ').trim();
		const adresse = `${p?.street1 ?? ''} ${p?.zip ?? ''} ${p?.city ?? ''}`.replace(/\s+/g, ' ').trim();
		return nom || adresse || ('Tiers n° ' + p?.id);
	}

	/** La liste, depuis /common/partner/{id}/links — la table telle qu'elle est, une ligne par
	 *  (rattaché, rôle), le tiers rattaché chargé. Relue après chaque écriture et au retour
	 *  d'une fiche ouverte depuis la liste : un nom a pu changer. */
	private loadRelations(): void {
		const id = this.id;
		if (!id) { this.relations = []; return; }
		this.relationsLoading = true;
		this.subs.push(this.partnerService.getPartnerLinks(id).pipe(take(1), catchError(() => of([] as any[]))).subscribe((rows: any[]) => {
			if (id !== this.id) return;		// réponse d'un tiers qu'on a quitté entre-temps
			this.relationsLoading = false;
			this.relations = (rows ?? [])
				.filter((l) => this.isRelationRole(+l.role))
				.map((l) => ({
					key: l.child + '/' + l.role,
					childId: +l.child,
					role: +l.role,
					roleName: this.relationRoleName(+l.role),
					label: this.relLabelOf(l.lnk_child_partner) || ('Tiers n° ' + l.child),
					isDefault: !!l.is_default,
					busy: false,
				}));
		}));
	}

	/** Rattacher : la ligne apparaît aussitôt, grisée, et part au serveur. */
	addRelation(): void {
		const p = this.newRelPartner;
		const role = this.newRelRole.value;
		if (!this.canAddRelation || !p?.id || role === null || role === undefined) return;
		if (+p.id === this.id) { this.alert.error('Un tiers ne peut pas être rattaché à lui-même.'); return; }
		// Le même couple (tiers, titre) deux fois : c'est la clé primaire de c_partner_partner qui
		// le refuserait — autant le dire ici.
		if (this.relations.some((r) => r.childId === +p.id && r.role === +role)) { this.alert.error('Cette relation existe déjà.'); return; }
		const row: RelationRow = {
			key: p.id + '/' + role, childId: +p.id, role: +role, roleName: this.relationRoleName(+role),
			label: this.relLabelOf(p), isDefault: false, busy: true,
		};
		this.relations = [...this.relations, row];
		this.newRelPartner = null;
		this.relSearchReset.next(true);		// libère le champ (et rebranche sa recherche) ; le titre reste choisi
		this.writeRelation(row, 'INSERT');
	}

	/** Retirer : la ligne se grise et part au serveur ; elle disparaît quand il a répondu. */
	removeRelation(r: RelationRow): void {
		if (r.busy) return;
		r.busy = true;
		this.writeRelation(r, 'DELETE');
	}

	/** UNE ligne c_partner_partner, avec son verbe, par l'event PARTNER que savePartnerLinks$
	 *  bâtit sur l'état frais du serveur. Un refus métier arrive en HTTP 200 avec success:false :
	 *  il se dit, et la liste est relue dans tous les cas — c'est le serveur qui a raison. */
	private writeRelation(r: RelationRow, action: 'INSERT' | 'DELETE'): void {
		const line = { parent: this.id, child: r.childId, role: r.role, is_default: r.isDefault, data: null, action };
		this.subs.push(this.partnerService.savePartnerLinks$(this.id, [line]).pipe(take(1)).subscribe({
			next: (rep: any) => {
				if (rep && rep.success === false) this.alert.error('Refus du serveur : ' + this.refusalText(rep));
				this.loadRelations();
			},
			error: (e: any) => {
				this.alert.error('ERREUR Serveur : ' + e);
				this.loadRelations();
			},
		}));
	}

	/** La fiche du tiers rattaché, PAR-DESSUS celle-ci — on ne la quitte pas : sa saisie en
	 *  cours reste, et ce que la commande recevra à sa fermeture reste CE tiers, pas l'autre.
	 *  Même fenêtre, même taille — le triplet est celui de QuotationComponent.openPartnerDialog,
	 *  à faire bouger ensemble. Au retour, les libellés sont relus : un nom a pu changer. */
	openRelated(r: RelationRow): void {
		const ref = this.dialog.open(PartnerComponent, {
			width: '96%',
			height: '94%',
			maxWidth: '96vw',
			data: { mode: 'DIALOG', id: r.childId },
		});
		this.subs.push(ref.afterClosed().pipe(take(1)).subscribe(() => this.loadRelations()));
	}

	// ─────────────────────────────────────────────────────────────────────────────────
	// ADRESSES — les lignes c_partner_partner de rôle -5 du formulaire
	//
	// L'INDEX `i` EST CELUI DE LA LISTE DES ADRESSES (lignes de rôle -5, ordre du serveur), pas
	// celui du FormArray des relations, qui mêle lignes d'adresse et lignes de qualité. `line(i)`
	// rend le FormGroup de l'ADRESSE — l'enfant de la ligne — pour que le gabarit, qui lit
	// ctrl(i, 'street1'), n'ait pas à connaître la ligne qui la porte. Les deux qualités sont des
	// lignes de rôle -1 et 2 : PartnerLinksForm les pose et les retire, une seule par rôle.
	// ─────────────────────────────────────────────────────────────────────────────────
	get addressLines(): FormGroup[] { return this.formGroup ? PartnerLinksForm.addressLines(this.formGroup) : []; }

	/** Index des lignes VIVANTES : une suppression est un drapeau DELETE, pas un retrait (le
	 *  serveur a besoin de la ligne pour la supprimer). Le gabarit itère TOUJOURS là-dessus, par liveBands. */
	get liveIndexes(): number[] {
		const out: number[] = [];
		this.addressLines.forEach((l, i) => { if (!PartnerLinksForm.isDeleted(l)) out.push(i); });
		return out;
	}

	get liveCount(): number { return this.liveIndexes.length; }

	/** Les bandes du gabarit : l'adresse (`child`, clé de suivi du @for) et sa position `i` dans addressLines, celle des ids p2-adr-{i}. */
	get liveBands(): { child: number; i: number }[] { return this.liveIndexes.map((i) => ({ child: this.childId(i), i })); }

	/** Le FormGroup de la i-ème ADRESSE (l'enfant de sa ligne de rôle -5). */
	line(i: number): FormGroup { return PartnerLinksForm.addressOf(this.addressLines[i]); }

	childId(i: number): number { return +this.addressLines[i]?.get('child')?.value; }

	ctrl(i: number, name: string): FormControl { return this.line(i)?.get(name) as FormControl; }

	/** Pays proposés à l'adresse i : les actifs de c_values 36, plus le sien s'il ne l'est plus (il reste lisible). Une liste par code, pour rendre toujours la même référence. */
	countryItems(i: number): { label: string; value: string }[] {
		const courant = this.ctrl(i, 'country')?.value ?? '';
		let items = this.countryItemsByCode.get(courant);
		if (!items) {
			items = this.countries.filter((c: any) => c?.is_active !== false || c?.data?.code === courant).map((c: any) => ({ label: c.name, value: c.data?.code }));
			this.countryItemsByCode.set(courant, items);
		}
		return items;
	}

	addressCufGroup(i: number): FormGroup { return this.line(i)?.get('data')?.get('cufs') as FormGroup; }

	isBill(i: number): boolean { return PartnerLinksForm.isDefault(this.formGroup, this.childId(i), constants.PARTNER_PARTNER_ROLE_BILL); }
	isDeliv(i: number): boolean { return PartnerLinksForm.isDefault(this.formGroup, this.childId(i), constants.PARTNER_PARTNER_ROLE_DELIV); }

	/** Exclusif par construction : la ligne de rôle -1 se pose ici et se retire de l'ancienne. */
	setBill(i: number): void {
		PartnerLinksForm.setDefault(this.formGroup, +this.formGroup.get('id')?.value, this.childId(i), constants.PARTNER_PARTNER_ROLE_BILL);
	}

	setDeliv(i: number): void {
		PartnerLinksForm.setDefault(this.formGroup, +this.formGroup.get('id')?.value, this.childId(i), constants.PARTNER_PARTNER_ROLE_DELIV);
	}

	/** 2ᵉ clic d'un double clic sur une gélule : sa bande est déjà remontée, il activerait l'adresse qui a pris sa place. */
	onFlagClick(e: MouseEvent): void { if (e.detail > 1) e.preventDefault(); }

	/** Nouvelle adresse VIDE, éditable sur place. La fiche officielle, elle, géocode puis
	 *  enregistre d'office : ici la saisie se fait dans la bande comme pour les autres, et le
	 *  géocodage attend le bouton « Localiser » ou l'enregistrement. La première adresse d'un
	 *  dossier porte les deux qualités. */
	addAddress(): void {
		const cufs = new FormGroup({});
		for (const cuf of this.addressCufs) if (cuf.id !== undefined && cuf.id !== null) cufs.addControl('' + cuf.id, new FormControl(null));
		cufs.addControl('' + this.CUF_LAT, new FormControl(null));
		cufs.addControl('' + this.CUF_LNG, new FormControl(null));
		const first = this.liveCount === 0;
		const adresse = this.fb.group({
			id: 0,							// remplacé par l'id provisoire négatif de la ligne (PartnerLinksForm.addAddress)
			typ: 1,							// convention : une adresse pure est un « particulier »
			civility: 0,
			firstname: null,
			name: null,
			roles: this.fb.array([constants.PARTNER_ROLE_ADDRESS]),		// « Adresse » : marque la ligne rattachée (le serveur l'écarte des recherches sur le lien -5)
			street1: new FormControl(''),
			street2: new FormControl(''),
			street3: new FormControl(''),
			zip: new FormControl(''),
			city: new FormControl(''),
			country: new FormControl('FR'),
			email: null,
			tel1: null,
			tel2: null,
			action: 'INSERT',
			data: this.fb.group({ cufs: cufs }),
		});
		PartnerLinksForm.addAddress(this.formGroup, +this.formGroup.get('id')?.value, adresse, { bill: first, deliv: first });
		this.formGroup.markAsDirty();
	}

	deleteAddress(i: number): void {
		PartnerLinksForm.deleteAddress(this.formGroup, this.addressLines[i]);
	}

	// ─────────────────────────────────────────────────────────────────────────────────
	// GÉOLOCALISATION — même contrat BAN que la fiche officielle
	// ─────────────────────────────────────────────────────────────────────────────────
	geoCtrl(i: number, cufId: number): FormControl | null { return (this.addressCufGroup(i)?.get('' + cufId) as FormControl) ?? null; }

	geoLat(i: number): number | null { return this.geoUnset(i) ? null : GeoService.numOf(this.geoCtrl(i, this.CUF_LAT)?.value); }
	geoLng(i: number): number | null { return this.geoUnset(i) ? null : GeoService.numOf(this.geoCtrl(i, this.CUF_LNG)?.value); }

	/** Le couple (0, 0) en base veut dire « non renseigné », pas une position au large de
	 *  l'Afrique : carte masquée, coordonnées affichées « — », et ligne géocodée au chargement
	 *  comme une ligne vide. */
	private geoUnset(i: number): boolean {
		return GeoService.numOf(this.geoCtrl(i, this.CUF_LAT)?.value) === 0 && GeoService.numOf(this.geoCtrl(i, this.CUF_LNG)?.value) === 0;
	}

	hasGeo(i: number): boolean { return this.geoLat(i) !== null && this.geoLng(i) !== null; }

	/** Libellé de la carte : exactement l'adresse envoyée au géocodage. */
	geoLabel(i: number): string { return GeoService.geoQueryOf(this.line(i)?.value ?? {}).label; }

	/** Le libellé RETENU par la BAN, pas seulement le point : c'est lui qui dit qu'un
	 *  « 9 CH DE LA CHAPELLE QUARTIER » a été résolu en « Allée de la Clairière ». */
	banLabel(i: number): string { return this.geoInfo.get(this.childId(i))?.label ?? ''; }

	/** Verdict non franc : la position est affichée, elle n'a PAS été écrite en base. On la
	 *  rattrape en déplaçant l'épingle sur la carte, ce qui la rend enregistrable. */
	banDoubt(i: number): boolean {
		const geo = this.geoInfo.get(this.childId(i));
		return !!geo && geo.trust !== 'exact';
	}

	geoBusy(i: number): boolean { return this.geoPending.has(this.childId(i)); }
	geoErr(i: number): string | null { return this.geoError.get(this.childId(i)) ?? null; }

	/** La position ACTUELLE d'une adresse dans la liste — -1 si elle n'y est plus (supprimée,
	 *  ou fiche rechargée sur un autre tiers). C'est par là qu'une réponse asynchrone retrouve
	 *  sa ligne, l'ordre ayant pu bouger depuis l'envoi. */
	private indexOfChild(child: number): number {
		return this.addressLines.findIndex((l) => +l.get('child')?.value === child && !PartnerLinksForm.isDeleted(l));
	}

	private geocodeMissing(): void {
		this.addressLines.forEach((l, i) => {
			if (PartnerLinksForm.isDeleted(l)) return;
			if (this.hasGeo(i)) return;
			this.onGeocode(i, true);
		});
	}

	/** Les lignes chargées ont déjà leurs controls géo (PartnerService concatène la structure) ;
	 *  défensif pour les autres — un ?.setValue sur un control absent échouerait EN SILENCE. */
	// Un control par cuf d'adresse sur CHAQUE ligne, quel que soit l'ordre d'arrivée du
	// référentiel et du tiers : un formControlName sur un control absent fait tomber le gabarit.
	private ensureAddressCufControls(): void {
		if (!this.formGroup) return;
		for (let i = 0; i < this.addressLines.length; i++) {
			const cufs = this.addressCufGroup(i);
			if (!cufs) continue;
			for (const c of this.addressCufs) if (c.id !== undefined && c.id !== null && !cufs.get('' + c.id)) cufs.addControl('' + c.id, new FormControl(null));
		}
	}

	private ensureGeoControls(i: number): void {
		const cufs = this.addressCufGroup(i);
		if (!cufs) return;
		for (const id of [this.CUF_LAT, this.CUF_LNG]) if (!cufs.get('' + id)) cufs.addControl('' + id, new FormControl(null));
	}

	/** auto = déclenché par le chargement, pas par l'utilisateur : aucun message à l'écran, et on
	 *  n'écrit les coordonnées que sur un verdict FRANC. Une position approximative posée en base
	 *  au premier enregistrement, sans que personne ne l'ait regardée, est exactement ce qu'il ne
	 *  faut pas faire. Sur demande explicite (bouton « Localiser »), l'utilisateur assume. */
	onGeocode(i: number, auto = false): void {
		const l = this.line(i);
		const child = this.childId(i);
		if (!l || this.geoPending.has(child)) return;
		this.ensureGeoControls(i);
		this.geoInfo.delete(child); this.geoError.delete(child);
		const query = GeoService.geoQueryOf(l.value);
		if (GeoService.isEmptyQuery(query)) {
			if (!auto) this.geoError.set(child, 'Adresse vide : saisir au moins rue, code postal ou ville');
			return;
		}
		this.geoPending.add(child);
		this.subs.push(this.geoService.geocodeOutcome$(query).subscribe(({ geo, injoignable }: GeoOutcome) => {
			this.geoPending.delete(child);
			// La ligne est retrouvée PAR SON CHILD, à la réponse : entre l'envoi et le retour,
			// une qualité posée a pu réordonner la liste, ou l'adresse a pu être supprimée.
			const j = this.indexOfChild(child);
			if (j < 0) return;
			if (!geo) { if (!auto) this.geoError.set(child, injoignable ? this.GEO_INJOIGNABLE : 'Adresse non localisée'); return; }
			this.geoInfo.set(child, geo);		// libellé BAN + verdict : affichés dans tous les cas
			if (auto && geo.trust !== 'exact') return;
			const latc = this.geoCtrl(j, this.CUF_LAT);
			const lngc = this.geoCtrl(j, this.CUF_LNG);
			latc?.setValue(geo.lat); lngc?.setValue(geo.lon);
			// Auto : 1 adresse reprise sur 94 444 a des coordonnées ; marquer ici griserait « Commandes » sur une fiche intacte.
			if (auto) this.autoGeo.add(child); else { latc?.markAsDirty(); lngc?.markAsDirty(); }
		}));
	}

	/** Les coordonnées SE SAISISSENT : relevé GPS d'un chauffeur, point pris sur une autre
	 *  carte, correction d'une position que la BAN place au centre de la commune. Le control
	 *  est le cuf lui-même, on lui passe donc un nombre — jamais la chaîne de l'input, qui
	 *  partirait telle quelle dans le JSONB. Une saisie vide efface la coordonnée (null),
	 *  ce qui masque la carte et rend l'adresse « non localisée » : c'est la voie pour
	 *  annuler une position fausse. */
	onCoordInput(i: number, which: 'lat' | 'lng', event: Event): void {
		const raw = (event.target as HTMLInputElement)?.value ?? '';
		const txt = raw.trim().replace(',', '.');		// le pavé numérique français rend une virgule
		const num = txt === '' ? null : Number(txt);
		if (num !== null && !isFinite(num)) return;		// saisie en cours (« 43. », « - ») : on ne touche à rien
		this.ensureGeoControls(i);
		const ctrl = this.geoCtrl(i, which === 'lat' ? this.CUF_LAT : this.CUF_LNG);
		ctrl?.setValue(num);
		ctrl?.markAsDirty();
		this.geoInfo.delete(this.childId(i));		// le libellé BAN ne décrit plus la position saisie
		this.geoError.delete(this.childId(i));
	}

	/** Épingle déplacée à la main sur la carte. Avec « Localiser » et la saisie directe des
	 *  deux coordonnées, la troisième voie de modification. */
	onMapCoords(i: number, c: { lat: number; lon: number }): void {
		this.ensureGeoControls(i);
		const latc = this.geoCtrl(i, this.CUF_LAT);
		const lngc = this.geoCtrl(i, this.CUF_LNG);
		latc?.setValue(c.lat); lngc?.setValue(c.lon);
		latc?.markAsDirty(); lngc?.markAsDirty();
		this.geoError.delete(this.childId(i));
		this.geoInfo.delete(this.childId(i));		// le libellé BAN ne décrit plus la position déplacée
	}

	// ─────────────────────────────────────────────────────────────────────────────────
	// PIÈCES JOINTES ET MÉMO DES ADRESSES ET DU TIERS
	//
	// Le stockage des attachs est GÉNÉRIQUE : un couple (objecttyp, objectid) et rien d'autre.
	// Une adresse étant une ligne c_partner, elle se rattache comme un tiers — objecttyp
	// PARTNER, objectid = l'id de la ligne. Aucune structure nouvelle, aucun modèle modifié.
	//
	// LA LECTURE PASSE PAR /docsbyobjectid, ET C'EST OBLIGÉ. Contrairement à la commande, dont
	// le SELECT agrège ses pièces (`?o=deliveries,attachs` → LATERAL JOIN sur objecttyp = 4),
	// /common/partner/{id} ignore purement et simplement la query string et ne renvoie AUCUN
	// lnk_attachs : vérifié dans common/src/partner/routes.rs, qui construit ses paramètres avec
	// `ComplexParamsPartner { id: Some(id), ..Default::default() }`. C'est pourquoi une première
	// version, qui demandait `?o=attachs`, perdait tout au rechargement — le serveur n'avait
	// jamais rien promis de tel.
	//
	// /docsbyobjectid/{objectid} rend les pièces actives d'un objet, sans contenu binaire.
	//   • Il ne filtre PAS sur objecttyp : un id d'adresse et un id de livraison qui coïncident
	//     rendraient les deux jeux. On filtre donc côté client sur objecttyp === PARTNER.
	//   • Depuis le 2026-09-14 son SELECT ramène aussi `seq`, `name` et `memo`
	//     (get_attachs_by_object_id, common/src/attach/model.rs) : le carrousel — LE MÊME
	//     MODULE que sur la fiche produit, app-image-carousel — affiche donc les noms, et le
	//     mémo se lit dans la ligne. Face à un serveur d'avant, `memo` est absent : le texte
	//     est alors relu par /attach/{id} pour la seule pièce is_default_memo (pas de contenu
	//     binaire, l'appel est léger), et les noms restent vides.
	// ─────────────────────────────────────────────────────────────────────────────────

	/** Une ligne neuve n'a pas encore d'id : rien ne peut s'y rattacher tant qu'elle n'est
	 *  pas enregistrée — l'attach a besoin d'un objectid, et ni 0 ni un provisoire négatif n'en
	 *  sont un. */
	canAttach(i: number): boolean { return this.addressId(i) > 0; }

	/** Le tiers a un id — il a été enregistré au moins une fois. Avant, ni pièce jointe ni
	 *  relation ne peuvent s'y accrocher : l'une comme l'autre veulent un objectid / un parent. */
	get isPersisted(): boolean { return this.id > 0; }

	/** Les pièces du tiers — instance STABLE (c'est l'entrée du carrousel), vide sans id. */
	get partnerAttachs(): common.Attach[] { return this.id > 0 ? this.attachList(this.id) : []; }

	/** L'objectid des pièces de la ligne : son id EN BASE, 0 tant qu'elle n'est pas enregistrée.
	 *  L'id provisoire négatif d'une ligne neuve n'est jamais rendu : tout ce qui s'accroche à
	 *  une adresse (mémo, pièces) passe par ici, et une garde `if (!objectid)` laissait passer -1
	 *  — des mémos rattachés à l'objectid -1 en base en témoignent. */
	addressId(i: number): number {
		const id = +(this.line(i)?.get('id')?.value ?? 0);
		return id > 0 ? id : 0;
	}

	attachsOf(i: number): common.Attach[] {
		const objectid = this.addressId(i);
		return objectid ? this.attachList(objectid) : [];
	}

	/** Le control du mémo, créé à la volée. Appelé depuis le gabarit : il doit rendre TOUJOURS
	 *  la même instance pour un même id, sans quoi la zone de texte serait recréée à chaque
	 *  cycle de détection. */
	memoCtrl(i: number): FormControl {
		const objectid = this.addressId(i);
		if (!objectid) return this.memoUnsaved;
		return this.memoCtrlFor(objectid);
	}

	/** Mémo du tiers lui-même (objectid = son id, lu par loadAttachsOf(this.id)) ; tiers neuf = la zone partagée désactivée, jamais memoCtrlFor(0). */
	get partnerMemoCtrl(): FormControl { return this.id > 0 ? this.memoCtrlFor(this.id) : this.memoUnsaved; }

	/** Crée le control ET son AUTO-ENREGISTREMENT. Le mémo ne part pas avec l'event du tiers :
	 *  c'est un attach, il a sa propre voie (event 603, qui crée la pièce si elle n'existe pas
	 *  encore). On écrit donc au fil de la frappe, une seconde après la dernière touche —
	 *  personne ne pense à « enregistrer » une consigne griffonnée dans un coin de fiche. */
	private memoCtrlFor(objectid: number): FormControl {
		let ctrl = this.memoCtrls.get(objectid);
		if (ctrl) return ctrl;
		ctrl = new FormControl('');
		this.memoCtrls.set(objectid, ctrl);
		this.subs.push(ctrl.valueChanges.pipe(debounceTime(1000), distinctUntilChanged())
			.subscribe(() => this.saveMemo(objectid)));
		return ctrl;
	}

	/** Les pièces de chaque adresse enregistrée, plus celles du tiers lui-même. */
	private loadAttachs(): void {
		for (const i of this.liveIndexes) {
			const objectid = this.addressId(i);
			if (objectid) this.loadAttachsOf(objectid);
		}
		if (this.id > 0) this.loadAttachsOf(this.id);
	}

	private loadAttachsOf(objectid: number): void {
		this.subs.push(
			this.http.get<any[]>(environment.server_url + '/docsbyobjectid/' + objectid)
				.pipe(take(1), catchError(() => of(null)))
				.subscribe((rows: any[] | null) => {
					// Lecture en échec : on n'en sait pas plus qu'avant — surtout pas « aucune
					// pièce », qui ferait CRÉER un second mémo au prochain envoi. Le mémo reste
					// en attente (voir saveMemo) et les vignettes gardent leur contenu.
					if (!rows) return;
					// Le filtre sur objecttyp est à NOTRE charge : l'endpoint ne connaît que l'objectid.
					// Trié par RANG (l'endpoint rend par id décroissant) : les flèches du carrousel
					// déplacent d'un rang, l'ordre affiché doit être celui des rangs.
					const mine = rows
						.filter((a) => +a?.objecttyp === +this.ATTACH_OBJECTTYP)
						.sort((x, y) => ((x.seq ?? Number.MAX_SAFE_INTEGER) - (y.seq ?? Number.MAX_SAFE_INTEGER)) || (+x.id - +y.id));

					// Le mémo : la pièce is_default_memo LA PLUS RÉCENTE. Il ne devrait y en avoir
					// qu'une, mais une version de cette fiche en a créé une par frappe (id 0 posté
					// à chaque envoi) et ces doublons sont en base : la plus récente porte le
					// dernier texte saisi, c'est elle qu'on lit et qu'on met à jour désormais.
					const memo = mine
						.filter((a) => +a?.id > 0 && a?.data?.['flags']?.includes('is_default_memo'))
						.reduce((best, a) => (!best || +a.id > +best.id ? a : best), null as any);
					this.memoAttachIds.set(objectid, +(memo?.id ?? 0));
					if (memo && typeof memo.memo === 'string') this.setMemoText(objectid, memo.memo);
					else if (memo) this.loadMemoText(objectid, +memo.id);		// serveur d'avant : sans colonne memo
					else this.setMemoText(objectid, '');						// pas de mémo en base : la zone part vide

					// Le carrousel reçoit CE tableau et le garde : on remplace son CONTENU, jamais
					// l'instance — sinon le composant continuerait d'afficher l'ancien.
					const list = this.attachList(objectid);
					list.length = 0;
					list.push(...this.attachService.generate_attachs_object_with_thumbnail(mine as common.Attach[]));
				})
		);
	}

	/** Un serveur d'avant le 2026-09-14 ne ramène pas `memo` dans /docsbyobjectid : on relit
	 *  la seule pièce qui le porte. */
	private loadMemoText(objectid: number, attachId: number): void {
		this.subs.push(
			this.http.get<any>(environment.server_url + '/attach/' + attachId)
				.pipe(take(1), catchError(() => of(null)))
				.subscribe((a: any) => this.setMemoText(objectid, a?.memo ?? ''))
		);
	}

	/** Le texte LU EN BASE devient la référence « enregistrée ». Il ne remplace celui de la zone
	 *  que si rien n'y a été tapé depuis le dernier enregistrement : une lecture (premier
	 *  affichage, rechargement après l'enregistrement de la fiche) n'écrase ni une frappe en
	 *  cours ni un envoi en vol. S'il y a bien une saisie en attente — tapée avant que les pièces
	 *  ne soient lues — elle part maintenant : l'id de l'attach est connu, pas de doublon. */
	private setMemoText(objectid: number, text: string): void {
		const ctrl = this.memoCtrlFor(objectid);
		const typed: string = ctrl.value ?? '';
		const dirty = this.memoSaving.has(objectid) || typed !== (this.memosSaved.get(objectid) ?? '');
		this.memosSaved.set(objectid, text);
		if (!dirty) ctrl.setValue(text, { emitEvent: false });		// pas d'auto-enregistrement sur une lecture
		else this.saveMemo(objectid);
	}

	/** Le tableau de pièces d'une adresse. Instance STABLE : c'est l'entrée du carrousel. */
	private attachList(objectid: number): common.Attach[] {
		let list = this.attachs.get(objectid);
		if (!list) { list = []; this.attachs.set(objectid, list); }
		return list;
	}

	/** Dépôt d'une pièce. Même enchaînement que la fiche article : une vignette optimiste tout
	 *  de suite (id 0 → le carrousel affiche un rouet), puis la vraie pièce quand le serveur a
	 *  rendu son id, et sa vignette chargée derrière. On modifie le CONTENU du tableau que le
	 *  carrousel tient déjà, jamais son instance. */
	onFileSelected(i: number, event: Event): void { this.uploadFrom(this.addressId(i), event); }

	/** Le dépôt d'une pièce sur le tiers lui-même : même chemin, objectid = l'id du tiers. */
	onPartnerFileSelected(event: Event): void { this.uploadFrom(this.id, event); }

	private uploadFrom(objectid: number, event: Event): void {
		const input = event.target as HTMLInputElement;
		const file = input?.files?.[0];
		if (!file || !objectid) return;
		input.value = '';		// sans ça, re-choisir le MÊME fichier n'émet plus rien
		const list = this.attachList(objectid);
		this.uploading.add(objectid);
		this.subs.push(this.attachService.uploadMediaWithPlaceholder$(file, this.ATTACH_OBJECTTYP, objectid, list)
			.subscribe({
				next: (step: any) => {
					const attach: common.Attach = step?.item;
					if (!attach) return;
					if (step.status === 'placeholder') { list.push(attach); return; }

					this.uploading.delete(objectid);
					const idx = list.findIndex((a) => a.seq === attach.seq);
					if (idx !== -1) list[idx] = attach;
					if (!attach.id) return;
					this.subs.push(this.attachService.loadThumbnailImage$(attach).pipe(take(1)).subscribe((blob: any) => {
						const url = URL.createObjectURL(blob);
						this.objectUrls.push(url);
						attach.thumbnailLocalUrl = url;
					}));
				},
				error: () => { this.uploading.delete(objectid); this.alert.error('ERREUR au dépôt du fichier'); },
			}));
	}

	/** Départ du curseur : on n'attend pas la seconde de l'auto-enregistrement. */
	onMemoBlur(i: number): void {
		const objectid = this.addressId(i);
		if (objectid) this.saveMemo(objectid);
	}

	onPartnerMemoBlur(): void { if (this.id > 0) this.saveMemo(this.id); }

	/** Écrit le mémo, et seulement s'il a bougé — sans quoi chaque passage dans la zone, chaque
	 *  rechargement, posterait un event de plus. L'event cible L'ATTACH (memoAttachIds) : `id: 0`
	 *  ne part qu'au tout premier envoi, quand aucun mémo n'existe en base, et l'id que le
	 *  serveur rend alors est retenu — le suivant est un UPDATE (process_attach_save_memo).
	 *  Deux gardes de plus :
	 *   • pièces pas encore lues (clé absente) : on ne poste rien, on ne sait pas s'il faut
	 *     créer ou mettre à jour ; la lecture, en arrivant, repasse ici (setMemoText) ;
	 *   • envoi en vol : on attend sa réponse — un 2ᵉ POST partirait encore en id 0 et ferait un
	 *     SECOND attach (le blur juste après la seconde de l'auto-enregistrement, typiquement) ;
	 *     la réponse repasse ici et repart si le texte a encore bougé entre-temps.
	 *  La souscription N'EST PAS dans `subs` : fermer la fiche pendant l'envoi l'annulerait, et
	 *  ngOnDestroy compte sur elle pour poser la dernière frappe. */
	private saveMemo(objectid: number): void {
		const ctrl = this.memoCtrls.get(objectid);
		const attachId = this.memoAttachIds.get(objectid);
		if (!ctrl || attachId === undefined || this.memoSaving.has(objectid)) return;
		const value: string = ctrl.value ?? '';
		if (value === (this.memosSaved.get(objectid) ?? '')) return;
		this.memoSaving.add(objectid);
		this.attachService.saveMemo$({
			id: attachId,
			userid: 0,						// renseigné par le service depuis la session
			objecttyp: this.ATTACH_OBJECTTYP,
			objectid: objectid,
			datcre: new Date(),
			memo: value,
		} as common.Attach).pipe(take(1)).subscribe({
			next: (response: common.ApiResponse) => {
				this.memoSaving.delete(objectid);
				// Refus métier : HTTP 200 mais success:false — le texte reste « à enregistrer ».
				if (response?.success === false) { this.alert.error(response.error_msg || response.msg || 'ERREUR à l’enregistrement du mémo'); return; }
				if (!attachId) {
					const created = +(response?.data?.id ?? 0);
					if (created) this.memoAttachIds.set(objectid, created);		// création : l'id évite le doublon au prochain envoi
					else { this.memoAttachIds.delete(objectid); this.loadAttachsOf(objectid); }		// pas d'id rendu : on le relit plutôt que de recréer
				}
				this.memosSaved.set(objectid, value);
				this.saveMemo(objectid);		// le texte a encore bougé pendant l'envoi : on repart, l'id est connu cette fois
			},
			error: () => { this.memoSaving.delete(objectid); this.alert.error('ERREUR à l’enregistrement du mémo'); },
		});
	}

	// ─────────────────────────────────────────────────────────────────────────────────
	// ENREGISTREMENT
	// ─────────────────────────────────────────────────────────────────────────────────
	onSubmit(): void {
		if (!this.formGroup || this.saving) return;
		// Le bouton est grisé sur formGroup.invalid, mais Entrée dans un champ soumet aussi le
		// formulaire : même refus ici, dit à l'écran. Les seules règles du formulaire sont celles
		// des cufs (CufComponent.applyRules) — obligatoire, bornes, entier.
		if (this.formGroup.invalid) { this.alert.error('Enregistrement refusé : corrigez les champs en rouge.'); return; }
		this.prepareAddresses();
		// date nue, entiers, flottants — jamais une chaîne ni un moment dans le JSONB
		CufComponent.normalize(this.partnerCufGroup, this.cufsStructure);
		CufComponent.normalize(this.partnerCufGroup, this.legalCufs);		// même groupe de controls, autre structure
		for (let i = 0; i < this.addressLines.length; i++) CufComponent.normalize(this.addressCufGroup(i), this.addressCufs);	// niveau (INT), code porte…
		this.formGroup.controls['civility'].setValue(parseInt(this.formGroup.controls['civility'].value));
		this.formGroup.controls['typpay'].setValue(parseInt(this.formGroup.controls['typpay'].value));
		this.saving = true;
		this.subs.push(this.partnerService.postPartnerEvent(this.eventtyp, this.formGroup).subscribe({
			next: (response: common.ApiResponse) => {
				this.saving = false;
				// REFUS MÉTIER : HTTP 200 mais success:false (process_partner_partners — lien vers un
				// autre tiers, adresse d'un autre, auto-liaison…). Rien n'a été écrit : la saisie
				// reste à l'écran, en chaînes pour les radios, et le motif du serveur se dit. Le
				// tenir pour un succès affichait « Tiers enregistré » puis, sur un tiers neuf,
				// rechargeait l'id 0 — une fiche blanche.
				if (!response || response.success === false) {
					this.restoreRadioStrings();
					this.alert.error('Enregistrement refusé : ' + this.refusalText(response));
					return;
				}
				const created = !this.isPersisted;
				this.id = response?.data?.partner_id ?? this.id;
				this.alert.success('Tiers enregistré');
				// EN PAGE, un tiers NEUF a désormais une adresse : l'URL la prend (/common/partner
				// sans id → /common/partner/:id, en remplaçant l'entrée d'historique, un retour
				// arrière ne doit pas rouvrir un formulaire vierge). Ce sont deux entrées de route
				// distinctes : le routeur recrée le composant, qui charge le tiers en arrivant —
				// pas de loadPartner() ici, il ferait le travail deux fois.
				if (created && !this.isDialog && this.id > 0) {
					this.formGroup.markAsPristine();		// enregistré : la garde de route ne doit rien demander en quittant /common/partner
					this.router.navigate(['/common/partner', this.id], { replaceUrl: true });
					return;
				}
				this.reloading = true;
				this.loadPartner();
			},
			error: (e: any) => {
				this.saving = false;
				// Les deux casts ci-dessus ont laissé le formulaire en entiers : sans ce retour en
				// chaîne, les radios Civilité et Paiement se vident de leur sélection à l'écran.
				this.restoreRadioStrings();
				this.alert.error('ERREUR Serveur : ' + e);
			},
		}));
	}

	/** Le motif d'un refus métier : le serveur le met dans `msg` ET `error_msg` (ApiResponse::err). */
	private refusalText(rep: any): string {
		return rep?.error_msg || rep?.msg || 'motif non précisé';
	}

	/** Ce que faisait app-addresses.onSubmit dans l'ancienne fiche : cast des entiers, et
	 *  passage en UPDATE des seules lignes touchées (les autres restent UNCHANGED, NOP côté back). */
	/** Avant POST : cast des entiers de chaque adresse, et passage en UPDATE des lignes dont
	 *  l'adresse a été touchée. L'unicité des défauts n'a plus à être forcée : PartnerLinksForm la
	 *  garantit à chaque pose. */
	private prepareAddresses(): void {
		for (const l of this.addressLines) {
			const a = PartnerLinksForm.addressOf(l);
			a.controls['civility']?.setValue(parseInt(a.controls['civility']?.value ?? 0));
		}
		// Les coordonnées de la localisation automatique partent avec cet envoi : la ligne passe en UPDATE comme avant.
		for (const child of this.autoGeo) {
			const j = this.indexOfChild(child);
			if (j >= 0) { this.geoCtrl(j, this.CUF_LAT)?.markAsDirty(); this.geoCtrl(j, this.CUF_LNG)?.markAsDirty(); }
		}
		PartnerLinksForm.markDirtyAddresses(this.formGroup);
	}

	/** FENÊTRE : rend à l'appelant le tiers TEL QU'IL EST EN BASE — l'état du dernier chargement
	 *  ou enregistrement (`persisted`), jamais la saisie en cours : la commande y relit son
	 *  client (nom, adresses, défauts) et n'a que faire d'une adresse pas encore enregistrée ou
	 *  d'une ligne en cours de suppression. Un tiers neuf jamais enregistré ne rend rien : la
	 *  commande garde son client. Si un rechargement suit un enregistrement, on l'attend.
	 *  En page, pas de croix (JP, 23/10) : on sort par le menu ou le retour arrière, sous la garde canLeave. */
	close(): void {
		if (!this.isDialog || this.saving || this.leavePrompt) return;		// croix grisée pendant l'envoi ; Échap sur la question ouverte ne la rouvre pas
		if (!this.hasUnsavedChanges) { this.closeDialog(); return; }
		this.confirmLeave().then((ok) => { if (ok) this.closeDialog(); });
	}

	private closeDialog(): void {
		if (this.reloading) { this.closeWhenReloaded = true; return; }
		this.dialogRef!.close(this.persisted ?? undefined);
	}

	/** Garde de sortie EN PAGE (menu du haut, retour arrière, autre tiers) : même question que la croix en fenêtre. */
	canLeave(): boolean | Promise<boolean> {
		if (this.saving) return false;		// partir désabonnerait le POST en vol (ngOnDestroy)
		return this.hasUnsavedChanges ? this.confirmLeave() : true;
	}

	/** Question en cours : un 2ᵉ clic de menu la partage au lieu d'en ouvrir une autre (une seule fenêtre Swal à la fois). */
	private leavePrompt: Promise<boolean> | null = null;

	private confirmLeave(): Promise<boolean> {
		return this.leavePrompt ??= Swal.fire({
			title: 'Modifications non enregistrées',
			text: 'Quitter la fiche sans enregistrer ?',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Quitter',
			cancelButtonText: 'Rester',
			focusCancel: true,		// Entrée par réflexe = rester : une saisie ne se perd jamais d'une touche
			willOpen: (popup) => liftToTopLayer(popup.parentElement),
		}).then((r) => !!r.isConfirmed).finally(() => { this.leavePrompt = null; });
	}
}

// La fenêtre (MatDialog, Angular 22) vit dans le top layer : sans cela la question est peinte DESSOUS, seul son voile assombrit le pourtour, et la croix reste muette.
// Même procédé que le voile de SpinnerComponent ; les styles en ligne annulent ceux que le navigateur pose sur [popover] et que SweetAlert2 ne redéfinit pas.
function liftToTopLayer(container: HTMLElement | null): void {
	if (!container || typeof container.showPopover !== 'function') return;
	container.setAttribute('popover', 'manual');
	Object.assign(container.style, { width: 'auto', margin: '0', border: '0', color: 'inherit' });
	try { container.showPopover(); } catch (e) { console.warn('[PartnerComponent] showPopover refusé', e); }
}

// Routes common/partner et common/partner/:id : sortir d'une fiche modifiée passe par la question de PartnerComponent.canLeave.
export const partnerCanDeactivate: CanDeactivateFn<PartnerComponent> = (component) => component.canLeave();
